import React, { useState } from "react";
import { GroupCreationFormData } from "../../types";
import { WarningIcon } from "@phosphor-icons/react";

interface GroupCreationFormProps {
  onSubmit: (data: GroupCreationFormData) => Promise<void>;
  isLoading?: boolean;
  error?: string;
}

const GroupCreationForm: React.FC<GroupCreationFormProps> = ({
  onSubmit,
  isLoading = false,
  error,
}) => {
  const [formData, setFormData] = useState<GroupCreationFormData>({
    name: "",
    adminEmail: "",
  });

  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    adminEmail?: string;
  }>({});

  const validateForm = (): boolean => {
    const errors: { name?: string; adminEmail?: string } = {};

    if (!formData.name.trim()) {
      errors.name = "Il nome del gruppo è obbligatorio";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Il nome del gruppo deve avere almeno 2 caratteri";
    }

    if (!formData.adminEmail.trim()) {
      errors.adminEmail = "L'email è obbligatoria";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      errors.adminEmail = "Inserisci un indirizzo email valido";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      // Error handling is done by parent component
    }
  };

  const handleInputChange = (
    field: keyof GroupCreationFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="group-setup-container">
      <div className="group-setup-form-header">
        <h1 className="group-setup-title">Crea il tuo gruppo</h1>
        <p className="group-setup-subtitle">
          Avvia uno spazio condiviso per coordinare regali con famiglia, amici o
          colleghi. Ogni occasione resta organizzata nello stesso posto.
        </p>
      </div>

      {error && (
        <div className="error-message">
          <WarningIcon size={20} weight="fill" />
          {error}
        </div>
      )}

      <form className="group-creation-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="groupName" className="form-label">
            Nome del gruppo
          </label>
          <input
            id="groupName"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="es. Famiglia Rossi"
            disabled={isLoading}
            className={`form-input ${validationErrors.name ? "error" : ""}`}
          />
          {validationErrors.name && (
            <div className="field-error">{validationErrors.name}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="adminEmail" className="form-label">
            La tua email (Amministratore)
          </label>
          <input
            id="adminEmail"
            type="email"
            value={formData.adminEmail}
            onChange={(e) => handleInputChange("adminEmail", e.target.value)}
            placeholder="latuaemail@esempio.com"
            disabled={isLoading}
            className={`form-input ${
              validationErrors.adminEmail ? "error" : ""
            }`}
          />
          {validationErrors.adminEmail && (
            <div className="field-error">{validationErrors.adminEmail}</div>
          )}
        </div>

        <button
          type="submit"
          className="create-group-button"
          disabled={isLoading}
        >
          {isLoading ? "Creazione in corso..." : "Crea gruppo"}
        </button>
      </form>

      <div className="setup-help">
        <h3>Cosa succede dopo?</h3>
        <ol>
          <li>
            <strong>Creiamo il gruppo</strong> con un link dedicato
          </li>
          <li>
            <strong>Inviti i partecipanti</strong> e, se serve, assegni chi li
            rappresenta
          </li>
          <li>
            <strong>Create eventi</strong> e condividete le liste per ogni
            occasione
          </li>
        </ol>
      </div>
    </div>
  );
};

export default GroupCreationForm;
